import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router-dom';
import type { ExternalStayInput, StayInput } from '@baerenfang/shared';

type State = {
  stay: (StayInput | ExternalStayInput) & { id: number };
  name: string;
  /** Set when the entry came from the external (off-roster) form. Switches
   * the confirmation chrome — the contact block is rendered and the "new
   * entry" link points back to /gast-abrechnung. */
  external?: boolean;
};

function chf(rappen: number, lang: string): string {
  return new Intl.NumberFormat(lang, { style: 'currency', currency: 'CHF' }).format(rappen / 100);
}

function dateLabel(date: string, lang: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Intl.DateTimeFormat(lang, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y!, m! - 1, d!)));
}

export function Confirmation() {
  const { t, i18n } = useTranslation();
  const loc = useLocation();
  const state = loc.state as State | null;
  if (!state?.stay) {
    return <Navigate to={state?.external ? '/gast-abrechnung' : '/abrechnung'} replace />;
  }
  const { stay, name, external } = state;
  const lang = i18n.resolvedLanguage ?? 'de';
  const totalPersonNights = stay.nights.reduce(
    (s, n) => s + n.members_adult + n.members_child + n.guests_adult + n.guests_child,
    0,
  );
  const newEntryPath = external ? '/gast-abrechnung' : '/abrechnung';
  // Narrowed contact block — present only on external submissions; the
  // shared schema makes these optional, the external form makes them
  // required, so by the time we get here for an external row they're all
  // populated strings.
  const ext = external
    ? {
        surname: stay.surname as string,
        street: stay.street as string,
        zipcode: stay.zipcode as string,
        town: stay.town as string,
        email: stay.email as string,
      }
    : null;

  return (
    <section aria-labelledby="conf-title" className="max-w-2xl">
      <h1 id="conf-title" className="text-xl sm:text-2xl font-semibold mb-2">
        {t('confirmation.title')}
      </h1>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
        {t('confirmation.thanks', { name })}
      </p>

      <div className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700 rounded-lg p-4 sm:p-6 space-y-4">
        {ext && (
          <>
            <Row label={t('externalStayForm.surname')} value={ext.surname} />
            <Row label={t('externalStayForm.street')} value={ext.street} />
            <Row
              label={`${t('externalStayForm.zipcode')} / ${t('externalStayForm.town')}`}
              value={`${ext.zipcode} ${ext.town}`}
            />
            <Row label={t('externalStayForm.email')} value={ext.email} />
          </>
        )}
        <Row
          label={t('confirmation.dates')}
          value={`${dateLabel(stay.check_in, lang)} – ${dateLabel(stay.check_out, lang)}`}
        />
        <Row label={t('confirmation.totalPersonNights')} value={String(totalPersonNights)} />
        <Row label={t('stayForm.stayoverChf')} value={chf(stay.stayover_rappen, lang)} />
        <Row label={t('stayForm.beveragesChf')} value={chf(stay.beverages_rappen, lang)} />
        {stay.notes && <Row label={t('stayForm.notes')} value={stay.notes} />}

        <details className="text-sm">
          <summary className="cursor-pointer select-none font-medium">
            {t('confirmation.perNight')}
          </summary>
          <ul className="mt-2 space-y-1">
            {stay.nights.map((n) => (
              <li
                key={n.date}
                className="flex justify-between border-b border-slate-100 dark:border-slate-800 py-1"
              >
                <span>{dateLabel(n.date, lang)}</span>
                <span className="tabular-nums">
                  {n.members_adult + n.members_child + n.guests_adult + n.guests_child}
                </span>
              </li>
            ))}
          </ul>
        </details>
      </div>

      <div className="mt-5 flex gap-2">
        <Link
          to={newEntryPath}
          className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 font-medium px-4 py-2.5 text-sm"
        >
          {t('confirmation.newEntry')}
        </Link>
        <Link
          to="/"
          className="rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 font-medium px-4 py-2.5 text-sm"
        >
          {t('confirmation.backToCalendar')}
        </Link>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <dt className="text-slate-600 dark:text-slate-400 col-span-1">{label}</dt>
      <dd className="col-span-2 font-medium break-words">{value}</dd>
    </div>
  );
}
